import { useMemo } from 'react';
import { TASK_AI_BUILDER_NAME, type TaskAiMachine } from '@shared/types/tasks';
import { useLoader } from '@/hooks/use-loader';
import { usePermission } from '@/hooks/usePermission';
import { useCurrentProject } from '@/lib/current-project';
import { listRunnableMachines } from '@/lib/projects-api';

/** How long the fleet's machines for a project are kept before they're asked for again. */
const AI_MACHINES_STALE_MS = 60_000;

/**
 * SF-T-174: the fleet machines the AI builder can be sent to in this project (online, with a confirmed
 * checkout: the Run dialog's list), for "AI builder on <machine>" in the pickers and the @ list. Asked for
 * only once `enabled` (a picker opened, an @ typed), by people who may start runs, then cached for the
 * project (the key starts with the project id). `machines` is null until they load; `failed` says they
 * couldn't, and `retry` asks again.
 */
export function useAiMachines(enabled: boolean): { machines: TaskAiMachine[] | null; failed: boolean; retry: () => void } {
  const canRun = usePermission('RUNS_START');
  const project = useCurrentProject()?.id;
  const loaded = useLoader((signal) => listRunnableMachines({ signal, project }), [project], {
    key: project ? `${project}:ai-machines` : undefined,
    staleMs: AI_MACHINES_STALE_MS,
    enabled: enabled && canRun && Boolean(project),
  });
  const list = loaded.data?.data?.machines;
  const machines = useMemo(() => (list ? list.map(({ id, name }) => ({ id, name })) : null), [list]);
  const failed = !loaded.loading && !list && (Boolean(loaded.error) || Boolean(loaded.data?.error));
  return { machines, failed, retry: loaded.reload };
}

/** Whether the AI builder on this machine matches what's typed in a picker's search ("jack", "ai builder on j"). */
export function aiMachineMatches(machine: TaskAiMachine, search: string): boolean {
  const q = search.trim().toLowerCase();
  const name = machine.name.toLowerCase();
  return !q || name.includes(q) || `${TASK_AI_BUILDER_NAME} on ${name}`.toLowerCase().startsWith(q);
}
